// ═══════════════════════════════════════════════════════════════════════
// Centre de configuration (Lot 1, Lot 2A) — éléments communs : accès à l'API /api/configuration, navigation,
// statuts, fenêtres de validation et de motif, résumé d'une simulation. Langage DRH, aucun code technique.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Users, AlertTriangle } from 'lucide-react';
import { Badge, Bouton, BandeauInfo, formaterDate } from '../../design/composants';
import { Fenetre, Champ, PiedFenetre } from '../temps/reglagesCommun';
import { SOURCES_PAIE, NIVEAUX_CONFIANCE_PAIE } from '../../constants/libellesHumains';

const API = '/api/configuration';
export const lireCfg = async (url, params) => (await axios.get(`${API}${url}`, { params })).data.data;
export const ecrireCfg = async (methode, url, corps) => (await axios({ method: methode, url: `${API}${url}`, data: corps })).data.data;
export const messageErreur = (e, defaut = 'Opération impossible. Réessayez.') => e?.response?.data?.message || defaut;
export const codeErreur = (e) => e?.response?.data?.code || null;

/** Domaines à règles du Centre : code serveur ↔ adresse lisible. */
// Dispositifs sociaux : règles réglées PAR DISPOSITIF (portée, droits par mandat) ; retenues : règles d'entreprise.
export const SLUG_DOMAINE = { TEMPS: 'temps', CONGES: 'conges', HEURES_SUP: 'heures-sup', DISPOSITIFS: 'dispositifs', RETENUES: 'retenues' };
export const DOMAINE_DU_SLUG = Object.fromEntries(Object.entries(SLUG_DOMAINE).map(([k, v]) => [v, k]));

export const NIVEAUX = [
  ['LOI', 'Loi ou règlement'], ['CONVENTION', 'Convention collective'], ['ACCORD', 'Accord d\'entreprise'], ['SOGAS', 'Politique SOGAS'],
];
export const SOURCES = SOURCES_PAIE;
export const CONFIANCES = NIVEAUX_CONFIANCE_PAIE;
export const NATURES_SOURCE_PACK = {
  LOI: 'Loi', DECRET: 'Décret', ARRETE: 'Arrêté', CONVENTION_COLLECTIVE: 'Convention collective', ACCORD_ENTREPRISE: 'Accord d\'entreprise', DECISION_SOGAS: 'Décision SOGAS',
  STATUTS_ORGANISATION: 'Statuts de l\'organisation', DECISION_AG: 'Décision d\'assemblée générale', DECISION_BUREAU: 'Décision du bureau',
};

const TONS_STATUT = {
  BROUILLON: 'neutre', A_VALIDER: 'alerte', PROGRAMMEE: 'info', PROGRAMME: 'info', ACTIVE: 'succes', ACTIF: 'succes',
  REMPLACEE: 'neutre', REMPLACE: 'neutre', ARCHIVEE: 'neutre', ANNULEE: 'neutre', ANNULE: 'neutre',
};
export const BadgeStatutRegle = ({ statut }) => (statut ? <Badge ton={TONS_STATUT[statut.code] || 'neutre'} point>{statut.libelle}</Badge> : null);

const TONS_BRANCHEMENT = { APPLIQUE: 'succes', LU_SANS_TEST: 'alerte', NON_BRANCHE: 'danger' };
export function BadgeBranchement({ branchement, discret = false }) {
  if (!branchement) return null;
  if (discret && branchement.etat === 'APPLIQUE') return null;
  const libelles = { APPLIQUE: 'Appliquée', LU_SANS_TEST: 'Effet non prouvé', NON_BRANCHE: 'Sans effet' };
  return <Badge ton={TONS_BRANCHEMENT[branchement.etat] || 'neutre'} title={branchement.libelle}>{libelles[branchement.etat] || branchement.etat}</Badge>;
}

/** Navigation du Centre. */
export function NavParametres({ actif }) {
  const liens = [
    ['accueil', '/parametres', 'Domaines'], ['a-valider', '/parametres/a-valider', 'À valider'],
    ['reglementation', '/parametres/reglementation', 'Réglementation'], ['journal', '/parametres/journal', 'Journal'],
  ];
  return (
    <nav aria-label="Paramètres" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '0 0 16px' }}>
      {liens.map(([cle, vers, libelle]) => (
        <Link key={cle} to={vers} className={`rh-btn ${actif === cle ? 'rh-btn-primaire' : ''}`} style={{ textDecoration: 'none' }} aria-current={actif === cle ? 'page' : undefined}>{libelle}</Link>
      ))}
    </nav>
  );
}

/** Résumé lisible d'une simulation conservée. */
export function ResumeSimulation({ simulation }) {
  if (!simulation) return <BandeauInfo ton="alerte" titre="Pas encore simulée">L'impact de cette version n'a pas encore été calculé.</BandeauInfo>;
  const r = simulation;
  return (
    <div className="rh-carte" style={{ padding: 12, marginBottom: 12 }}>
      <p style={{ margin: '0 0 6px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Users size={16} aria-hidden="true" /> Simulation au {formaterDate(r.date_reference)}{r.cree_le ? ` (calculée le ${formaterDate(r.cree_le)})` : ''}
      </p>
      <p style={{ margin: '0 0 6px', fontSize: 14 }}>
        {r.population} salarié(s) concerné(s) · <strong>{r.changes} changement(s)</strong> · {r.inchanges} sans changement · aucune donnée modifiée
      </p>
      {r.repartition && (
        <ul style={{ margin: '0 0 6px 18px', fontSize: 13, color: 'var(--sg-texte-2)' }}>
          {Object.entries(r.repartition).map(([k, n]) => <li key={k}>{k} : {n} salarié(s)</li>)}
        </ul>
      )}
      {(r.anomalies || []).map((a) => (
        <p key={a.code + (a.regle || '')} style={{ margin: '0 0 4px', fontSize: 13, color: 'var(--sg-alerte, #9a6700)', display: 'flex', gap: 6 }}>
          <AlertTriangle size={14} aria-hidden="true" />{a.regle ? `${a.regle} : ` : ''}{a.message}
        </p>
      ))}
      {(r.exemples || []).length > 0 && (
        <details style={{ fontSize: 13 }}>
          <summary>Exemples ({r.exemples.length})</summary>
          <table className="rh-tableau" style={{ marginTop: 6 }}>
            <thead><tr><th>Salarié</th><th>Site</th><th>Avant</th><th>Après</th></tr></thead>
            <tbody>{r.exemples.map((x) => <tr key={x.matricule}><td>{x.nom}</td><td>{x.site || '—'}</td><td>{x.avant}</td><td>{x.apres}</td></tr>)}</tbody>
          </table>
        </details>
      )}
    </div>
  );
}

/** Validation : commentaire et confirmation explicite obligatoires. */
export function FenetreValider({ titre = 'Valider', resume, onFermer, onValider }) {
  const [commentaire, setCommentaire] = useState('');
  const [verifie, setVerifie] = useState(false);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  return (
    <Fenetre titre={titre} Icone={CheckCircle2} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {resume}
      <Champ libelle="Commentaire de validation" requis><textarea className="rh-champ-saisie" rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} /></Champ>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, marginBottom: 12 }}>
        <input type="checkbox" checked={verifie} onChange={(e) => setVerifie(e.target.checked)} />
        J'ai vérifié la valeur, la source, la date d'effet et la simulation.
      </label>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="primaire" Icone={CheckCircle2} disabled={enCours || !verifie || !commentaire.trim()}
          onClick={async () => { setEnCours(true); try { await onValider(commentaire); } catch (e) { setErreur(messageErreur(e)); setEnCours(false); } }}>Valider</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

/** Refus ou annulation : motif obligatoire. */
export function FenetreMotifCfg({ titre, aide, libelleBouton, onFermer, onConfirmer }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  return (
    <Fenetre titre={titre} Icone={XCircle} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {aide && <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--sg-texte-2)' }}>{aide}</p>}
      <Champ libelle="Motif" requis><textarea className="rh-champ-saisie" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="danger" Icone={XCircle} disabled={motif.trim().length < 3}
          onClick={async () => { try { await onConfirmer(motif); } catch (e) { setErreur(messageErreur(e)); } }}>{libelleBouton || titre}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

export const LIBELLES_ACTIONS_JOURNAL = {
  CONFIG_VERSION_PREPAREE: 'Version préparée', CONFIG_BROUILLON_MODIFIE: 'Brouillon modifié', CONFIG_SIMULATION: 'Simulation calculée',
  CONFIG_VERSION_SOUMISE: 'Soumise à validation', CONFIG_VERSION_VALIDEE: 'Validée', CONFIG_VERSION_REFUSEE: 'Refusée',
  CONFIG_VERSION_ANNULEE: 'Annulée', CONFIG_BROUILLON_RETIRE: 'Proposition retirée', CONFIG_PACK_CREE: 'Pack créé', CONFIG_PACK_SOUMIS: 'Pack soumis',
  CONFIG_PACK_VALIDE: 'Pack validé', CONFIG_PACK_REFUSE: 'Pack refusé', CONFIG_PACK_ANNULE: 'Pack annulé',
  CONFIG_PACK_SIMULE: 'Pack simulé', CONFIG_PACK_REGLE_RETIREE: 'Règle retirée d\'un pack',
};
